const API_CACHE_PREFIX = 'ysm:api-cache:'
const API_CACHE_INDEX_KEY = `${API_CACHE_PREFIX}index`

export const DEFAULT_API_CACHE_TTL = 15 * 1000
export const STATIC_API_CACHE_TTL = 5 * 60 * 1000
export const VOLATILE_API_CACHE_TTL = 5 * 1000

/** 配置类 / 公开列表：可缓存较久 */
export const STATIC_CACHE_PATTERNS = [
  /\/api\/v1\/webViewConfig\/?$/,
  /\/api\/v1\/lottery\/prizes\/?$/,
  /\/user\/hm2\/site-info\/?$/,
]

/** 账户 / 余额 / 订单 / 状态：短缓存（白名单） */
export const VOLATILE_CACHE_PATTERNS = [
  /\/user\/usersProfile\/?$/,
  /\/user\/check-in\/?$/,
  /\/user\/fundFlowList\/?$/,
  /\/user\/bankCard\/?$/,
  /\/user\/withdraw\/?$/,
  /\/user\/addresses\/?$/,
  /\/user\/referral\/reward\/list\/?$/,
  /\/user\/detail\/[^/]+\/getPromotions\/?$/,
  /\/api\/v1\/mall\/orders\/?$/,
  /\/api\/v1\/mall\/products\/?$/,
  /\/api\/v1\/lottery\/records\/?$/,
  /\/user\/hm2\/overview\/?$/,
  /\/user\/hm2\/earnings\/?$/,
  /\/user\/hm2\/invite\/?$/,
  /\/user\/hm2\/alipay-bind-status\/?$/,
  /\/user\/hm2\/welfare\/?$/,
  /\/user\/hm2\/withdraw-password\/?$/,
  /\/user\/hm2\/transfer\/?$/,
  /\/user\/hm2\/nodes\/?$/,
]

/** 有副作用或不该缓存的 GET */
export const NO_CACHE_PATTERNS = [
  /\/api\/v1\/app-download-log\/?$/,
  // 首页弹窗通知：后台刚发布后必须立刻可见，不能被 session 缓存挡掉
  /\/api\/v1\/notification\/?$/,
]

/** 变更后需立刻失效的相关缓存分组 */
const INVALIDATION_GROUPS = {
  wallet: [
    /\/user\/usersProfile\/?$/,
    /\/user\/hm2\/overview\/?$/,
    /\/user\/hm2\/earnings\/?$/,
    /\/user\/hm2\/transfer\/?$/,
    /\/user\/fundFlowList\/?$/,
    /\/user\/hm2\/nodes\/?$/,
    /\/api\/v1\/mall\/orders\/?$/,
  ],
  profile: [
    /\/user\/usersProfile\/?$/,
    /\/user\/hm2\/overview\/?$/,
    /\/user\/hm2\/invite\/?$/,
    /\/user\/hm2\/alipay-bind-status\/?$/,
    /\/user\/referral\/reward\/list\/?$/,
    /\/user\/detail\/[^/]+\/getPromotions\/?$/,
  ],
  bank: [
    /\/user\/bankCard\/?$/,
    /\/user\/hm2\/overview\/?$/,
  ],
  auth: [], // 登录态变化清全部
}

/** 写缓存世代号：clear 后递增，防止旧请求回写脏数据 */
let cacheGeneration = 0

function getCacheStorage() {
  try {
    return sessionStorage
  } catch (e) {
    return null
  }
}

function stableStringify(value) {
  if (value == null) return ''
  if (typeof value !== 'object') return String(value)
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`
  }
  const keys = Object.keys(value).sort()
  return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`
}

function hashString(input) {
  const str = String(input || '')
  let hash = 0
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    hash |= 0
  }
  return `h${Math.abs(hash)}`
}

function readIndex(storage) {
  try {
    const raw = storage.getItem(API_CACHE_INDEX_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

function writeIndex(storage, keys) {
  try {
    storage.setItem(API_CACHE_INDEX_KEY, JSON.stringify(keys))
  } catch (e) {
    // ignore quota errors
  }
}

function trackCacheKey(storage, cacheKey) {
  const index = readIndex(storage)
  if (!index.includes(cacheKey)) {
    index.push(cacheKey)
    writeIndex(storage, index)
  }
}

function untrackCacheKey(storage, cacheKey) {
  const index = readIndex(storage).filter((key) => key !== cacheKey)
  writeIndex(storage, index)
}

export function getCacheGeneration() {
  return cacheGeneration
}

export function getRequestUrl(config) {
  const raw = config.url || ''
  try {
    if (/^https?:\/\//i.test(raw)) {
      return new URL(raw).pathname
    }
    const base = config.baseURL || ''
    if (base && /^https?:\/\//i.test(base)) {
      return new URL(raw, base).pathname
    }
  } catch (e) {
    // fall through
  }
  return raw.split('?')[0]
}

function matchAny(url, patterns) {
  return patterns.some((pattern) => pattern.test(url))
}

export function isStaticCacheUrl(url) {
  return matchAny(url, STATIC_CACHE_PATTERNS)
}

export function isVolatileCacheUrl(url) {
  return matchAny(url, VOLATILE_CACHE_PATTERNS)
}

/** 仅白名单可缓存；未知 GET 一律不缓存，避免脏读 */
export function isCacheableRequest(config) {
  if (!config || config.skipApiCache || config.force || config.forceRefresh) {
    return false
  }
  const method = (config.method || 'get').toLowerCase()
  if (method !== 'get') return false

  const url = getRequestUrl(config)
  if (matchAny(url, NO_CACHE_PATTERNS)) return false
  return isStaticCacheUrl(url) || isVolatileCacheUrl(url)
}

export function getCacheTTL(config) {
  if (typeof config.cacheTTL === 'number') return config.cacheTTL
  const url = getRequestUrl(config)
  if (isStaticCacheUrl(url)) return STATIC_API_CACHE_TTL
  if (isVolatileCacheUrl(url)) return VOLATILE_API_CACHE_TTL
  return DEFAULT_API_CACHE_TTL
}

export function getCacheKey(config, token) {
  if (config.cacheKey) {
    return `${API_CACHE_PREFIX}${config.cacheKey}`
  }

  const baseURL = config.baseURL || process.env.VUE_APP_BASE_API || ''
  const url = getRequestUrl(config)
  const params = stableStringify(config.params || {})
  const scope = token ? `u:${hashString(token)}` : 'public'

  return `${API_CACHE_PREFIX}${scope}:${baseURL}:${url}:${params}`
}

export function readApiCache(cacheKey, allowStale = false) {
  const storage = getCacheStorage()
  if (!storage || !cacheKey) return null

  try {
    const raw = storage.getItem(cacheKey)
    if (raw == null) return null

    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !('data' in parsed)) {
      return null
    }

    // 世代号不匹配：视为失效
    if (
      typeof parsed.generation === 'number' &&
      parsed.generation !== cacheGeneration
    ) {
      storage.removeItem(cacheKey)
      untrackCacheKey(storage, cacheKey)
      return null
    }

    const expired =
      typeof parsed.expiresAt === 'number' && parsed.expiresAt <= Date.now()
    if (expired && !allowStale) {
      storage.removeItem(cacheKey)
      untrackCacheKey(storage, cacheKey)
      return null
    }
    return parsed.data
  } catch (e) {
    return null
  }
}

export function writeApiCache(cacheKey, data, ttl, generation = cacheGeneration) {
  const storage = getCacheStorage()
  if (!storage || !cacheKey || typeof ttl !== 'number' || ttl <= 0) return

  // 写入时世代已变：丢弃，防止旧请求覆盖新数据
  if (generation !== cacheGeneration) return

  const entry = {
    data,
    expiresAt: Date.now() + ttl,
    generation: cacheGeneration,
    updatedAt: Date.now(),
  }

  try {
    storage.setItem(cacheKey, JSON.stringify(entry))
    trackCacheKey(storage, cacheKey)
  } catch (e) {
    try {
      const index = readIndex(storage)
      index.slice(0, Math.min(5, index.length)).forEach((key) => {
        storage.removeItem(key)
        untrackCacheKey(storage, key)
      })
      if (generation !== cacheGeneration) return
      storage.setItem(cacheKey, JSON.stringify(entry))
      trackCacheKey(storage, cacheKey)
    } catch (err) {
      // ignore
    }
  }
}

export function clearApiCache() {
  cacheGeneration += 1
  const storage = getCacheStorage()
  if (!storage) return

  try {
    const index = readIndex(storage)
    index.forEach((key) => storage.removeItem(key))
    storage.removeItem(API_CACHE_INDEX_KEY)

    const keysToRemove = []
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i)
      if (key && key.startsWith(API_CACHE_PREFIX)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => storage.removeItem(key))
  } catch (e) {
    // ignore
  }
}

/** 按 URL 模式清除部分缓存（保留静态配置） */
export function invalidateApiCacheByPatterns(patterns = []) {
  if (!patterns.length) {
    clearApiCache()
    return
  }

  cacheGeneration += 1
  const storage = getCacheStorage()
  if (!storage) return

  const matchKey = (key, pattern) => {
    if (pattern.test(key)) return true
    // cacheKey 含前缀/baseURL/params，去掉末尾锚点再匹配
    try {
      const loose = new RegExp(pattern.source.replace(/\$$/, ''), pattern.flags)
      return loose.test(key)
    } catch (e) {
      return key.includes(String(pattern))
    }
  }

  try {
    const index = readIndex(storage)
    const remain = []
    index.forEach((key) => {
      const hit = patterns.some((pattern) => matchKey(key, pattern))
      if (hit) {
        storage.removeItem(key)
      } else {
        remain.push(key)
      }
    })
    writeIndex(storage, remain)
  } catch (e) {
    clearApiCache()
  }
}

export function invalidateApiCacheGroup(groupName) {
  if (groupName === 'auth' || !INVALIDATION_GROUPS[groupName]) {
    clearApiCache()
    return
  }
  invalidateApiCacheByPatterns(INVALIDATION_GROUPS[groupName])
}

/** 根据变更接口推断应失效的缓存 */
export function invalidateAfterMutation(config) {
  if (!config) {
    clearApiCache()
    return
  }
  const url = getRequestUrl(config)
  const method = (config.method || 'get').toLowerCase()
  if (method === 'get') return

  if (
    /\/user\/hm2\/nodes\/buy\/?$/.test(url) ||
    /\/user\/hm2\/transfer\/?$/.test(url) ||
    /\/user\/withdraw\/?$/.test(url) ||
    /\/user\/check-in\/?$/.test(url) ||
    /\/user\/realNameAuth\/?$/.test(url) ||
    /\/api\/v1\/mall\//.test(url) ||
    /\/api\/v1\/payment\//.test(url)
  ) {
    invalidateApiCacheGroup('wallet')
    return
  }

  if (
    /\/user\/hm2\/withdraw-password\/?$/.test(url) ||
    /\/user\/bankCard/.test(url) ||
    /\/set_alipay_account\/?$/.test(url) ||
    /\/set_wechat_account\/?$/.test(url)
  ) {
    invalidateApiCacheGroup('bank')
    return
  }

  if (
    /\/user\/detail\//.test(url) ||
    /\/user\/hm2\/invite\/?$/.test(url) ||
    /changeRealName|changePassword|changeEmail|changePhoneNumber/.test(url)
  ) {
    invalidateApiCacheGroup('profile')
    return
  }

  // 其余写操作：整表清空，保证一致性
  clearApiCache()
}
